/**
 * Retry policy for attachment background uploads.
 *
 * Web port of `kaether_for_ios/Stoat/E2EE/AttachmentBackgroundUploadPolicy.swift`.
 *
 * ## Backoff schedule
 *
 * Byte-for-byte match with iOS:
 *
 *   Attempt 1 → wait  2s
 *   Attempt 2 → wait  8s
 *   Attempt 3 → wait 30s
 *   Attempt 4 → wait 120s
 *   Attempt 5 → wait 300s
 *   Attempt 6 → wait 300s  (cap — repeats 300s from here)
 *
 * 6-retry cap: after 6 total retries the upload transitions to `failed`.
 *
 * ## `shouldRetry(errorCode)`
 *
 * Transient errors (server-side temporary failures) → retry.
 * Permanent errors (client errors, auth failures, gone) → do NOT retry.
 *
 * The distinction:
 *   - HTTP 4xx (except 408, 425, 429) → permanent (bad request, auth, not found)
 *   - HTTP 408 Request Timeout → transient
 *   - HTTP 425 Too Early → transient
 *   - HTTP 429 Too Many Requests → transient (rate-limited; backoff applies)
 *   - HTTP 5xx → transient (server-side temporary failure)
 *   - "networkError" → handled by the `networkLost` path, not here
 *   - Unknown / non-numeric → permanent (safe default)
 */

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * Backoff intervals in **seconds** (not milliseconds), mirroring iOS exactly.
 * Index 0 = first retry, index 5 = sixth retry (cap).
 * If `retryCount >= BACKOFF_SCHEDULE_S.length`, use the last entry (300s).
 */
export const BACKOFF_SCHEDULE_S: readonly number[] = [2, 8, 30, 120, 300, 300];

/** Maximum number of retries before transitioning to `failed`. */
export const MAX_RETRY_COUNT = 6;

// ---------------------------------------------------------------------------
// shouldRetry
// ---------------------------------------------------------------------------

/**
 * Returns `true` if the given HTTP status code or error string should trigger
 * a retry (with backoff), or `false` if it is a permanent failure that must
 * immediately transition the upload to `failed`.
 *
 * @param errorCode - HTTP status as a decimal string (e.g. `"503"`) or a
 *   transport error string (e.g. `"networkError"`).
 */
export function shouldRetry(errorCode: string): boolean {
  const status = parseInt(errorCode, 10);
  if (isNaN(status)) {
    // Non-numeric (e.g. "networkError", "aborted") — treat as permanent so the
    // caller's `networkLost` path handles network failures separately.
    return false;
  }

  // 2xx / 3xx — not an error; caller should not reach here, but be defensive.
  if (status < 400) return false;

  // Specific 4xx treated as transient:
  if (status === 408) return true; // Request Timeout
  if (status === 425) return true; // Too Early (TLS / resumable upload)
  if (status === 429) return true; // Too Many Requests (rate limit)

  // All other 4xx → permanent (bad request, auth failure, not found, etc.)
  if (status >= 400 && status < 500) return false;

  // 5xx → transient (server-side temporary failure)
  if (status >= 500) return true;

  return false;
}

// ---------------------------------------------------------------------------
// backoffDelayMs
// ---------------------------------------------------------------------------

/**
 * Return the backoff delay in **milliseconds** for the given `retryCount`.
 *
 * `retryCount` is 1-based (first retry = 1, sixth retry = 6).
 * Values beyond the schedule length are clamped to the last entry (300 s).
 *
 * @param retryCount - Number of retries consumed so far (including this one).
 */
export function backoffDelayMs(retryCount: number): number {
  const idx = Math.max(0, Math.min(retryCount - 1, BACKOFF_SCHEDULE_S.length - 1));
  return BACKOFF_SCHEDULE_S[idx]! * 1000;
}

/**
 * Returns `true` if the upload has exhausted all allowed retries and must
 * transition to `failed`.
 */
export function isRetryCapExhausted(retryCount: number): boolean {
  return retryCount >= MAX_RETRY_COUNT;
}
